import { isUUID } from "@medplum/core";
import { Resource } from "@medplum/fhirtypes";

import structuredClone from "@ungap/structured-clone";
import { formatISO, parseISO } from "date-fns";
import ISO6391 from "iso-639-1";
import isEqual from "lodash/isEqual";
import { EnumLike, z } from "zod";

import { Systems } from "@/fhir/enums/systems";
import { isHtmlEmpty } from "@/utils/rich-text";

export const REQUIRED_FIELD_MESSAGE = "This field is required";

export const SIGNATURE_INITIALS_REQUIRED_MESSAGE = "Select at least one signature";

const phoneRegex = /^[\d\s\-().+]*$/;

export function zStringReq(msg?: string, description?: string) {
  return z
    .string({ description })
    .trim()
    .min(1, { message: msg ?? REQUIRED_FIELD_MESSAGE });
}

/**
 * Returns a Zod schema for a required rich-text field.
 * Rich text editors store an empty document as `<p></p>` rather than `""`, so this validates
 * on extracted text content via `isHtmlEmpty` instead of the raw HTML string length.
 *
 * @param fieldName - Named in the error message as "`${fieldName} is required`". Omit for the
 * generic required-field message.
 */
export function zHtmlRequired(fieldName?: string) {
  return z
    .string()
    .trim()
    .refine((value) => !isHtmlEmpty(value), {
      message: fieldName ? `${fieldName} is required` : REQUIRED_FIELD_MESSAGE,
    });
}

export function zPhoneNumber(msg?: string) {
  return z
    .string()
    .trim()
    .regex(phoneRegex, msg ?? "Invalid phone number format");
}

// The pipe skips the format check on an empty email, so the field reads as required. Mantine's
// zodResolver keeps the last issue for a path, which would otherwise be the format message.
export function zEmail(msg?: string) {
  return z
    .string()
    .trim()
    .min(1, msg ?? "Email is required")
    .pipe(z.string().email("Invalid email address"));
}

/**
 * Returns a Zod schema for an optional number.
 * This is needed because Mantine's NumberInput returns an empty string instead of undefined when
 * the input is cleared.
 */
export function zNumberOptional({
  min,
  max,
  positive = false,
}: { min?: number; max?: number; positive?: boolean } = {}) {
  let zNumber = z.number();
  if (min !== undefined) {
    zNumber = zNumber.min(min);
  }
  if (max !== undefined) {
    zNumber = zNumber.max(max);
  }
  if (positive) {
    zNumber = zNumber.positive();
  }
  return zNumber.optional().or(z.literal("").transform(() => undefined));
}

/**
 * Returns a Zod schema for a signature field, which holds an array of practitioner ids.
 * At least one signer is always required. Anything that is not an array of strings (an
 * unset field, a cleared Mantine MultiSelect) is treated as empty so the user sees the
 * required message instead of a type error.
 */
export function zSignatureInitialsRequired() {
  return z
    .preprocess(
      (value) => (Array.isArray(value) ? value.filter((entry) => typeof entry === "string") : []),
      z.array(z.string()),
    )
    .refine((value) => value.length > 0, { message: SIGNATURE_INITIALS_REQUIRED_MESSAGE });
}

export const allLanguages = ISO6391.getAllNames();

export function zLanguageEnum() {
  return z.enum([allLanguages[0], ...allLanguages.slice(1)]);
}

/**
 * Returns a Zod schema for an optional native enum.
 * This is needed because Mantine's Select component returns null instead of undefined when
 * the input is cleared.
 */
export function zNativeEnumOptional<T extends EnumLike>(values: T) {
  return z
    .nativeEnum(values)
    .nullish()
    .transform((value) => (value === null ? undefined : value));
}

export function zLOINCCode() {
  return z.object({
    system: z.literal(Systems.LOINC),
    code: zStringReq(),
    display: zStringReq(),
  });
}

export function zCoding() {
  return z.object(
    {
      system: zStringReq(),
      code: zStringReq(),
      display: zStringReq(),
    },
    { message: "This field is required" },
  );
}

export function zCodingOptional() {
  return z
    .object({
      system: z.string().trim().optional(),
      code: z.string().trim().optional(),
      display: z.string().trim().optional(),
    })
    .optional();
}

export function zRatioInput() {
  return z.object({
    numerator: z.object({
      comparator: z.string().optional(),
      value: z.number().nullable(),
      unit: zStringReq(),
    }),
    denominator: z.object({
      comparator: z.string().optional(),
      value: z.number().nullable(),
      unit: zStringReq(),
    }),
  });
}

// To use with Others case
export function zCodingWithText() {
  return zCoding().extend({
    text: z.string().optional(),
  });
}

export function zReference(expectedResourceType: string) {
  return z
    .string()
    .nullish()
    .transform((val) => val || undefined)
    .refine(
      (value) => {
        if (!value) return true;
        const [resourceType, id] = value.split("/");
        // TODO: Remove this once we have a way to check the resource type from the reference
        // return isResourceType(resourceType) && isUUID(id);
        return isEqual(resourceType, expectedResourceType) && isUUID(id);
      },
      {
        message: "This field is required",
      },
    );
}

export function isFormEmpty<T extends object>(form: T): boolean {
  const formClone = structuredClone(form);

  // Drop undefined values:
  Object.keys(formClone).forEach((key) => {
    if (formClone[key as keyof T] === undefined) {
      delete formClone[key as keyof T];
    }
  });

  // Check if the form is empty:
  return Object.keys(formClone).length === 0;
}

export function requiredTogether<T extends object>(form: T, keys: (keyof T)[]) {
  // If some keys are undefined/null, but not all are:
  if (keys.some((key) => form[key] == null) && !keys.every((key) => form[key] == null)) {
    return keys
      .filter((key) => form[key] == null)
      .map((key) => ({
        path: [key as string],
        message: REQUIRED_FIELD_MESSAGE,
      }));
  }
  return [];
}

export function addRequiredTogetherIssues<T extends object>(
  form: T,
  ctx: z.RefinementCtx,
  keys: (keyof T)[],
) {
  const issues = requiredTogether(form, keys);
  issues.forEach(({ path, message }) => {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path,
      message,
    });
  });
}

/**
 * Preprocessor for Mantine v8 DateInput fields.
 *
 * Mantine v8's DateInput fires `onChange` with an ISO date string (e.g. "2024-01-02")
 * instead of a Date object. This coercer normalises all incoming values so Zod's
 * `z.date()` validator can accept them.
 *
 * Converts:
 *  - ISO date strings  → Date object
 *  - `""` or `undefined` → `null`   (treated as "not set")
 *  - `null`            → `null`     (passed through)
 *  - Date objects      → unchanged
 */
export function coerceDateInput(val: unknown): Date | null | undefined {
  if (val === null || val === undefined || val === "") return null;
  if (val instanceof Date) return val;
  if (typeof val === "string") return parseISO(val);
  return undefined;
}

type ZDateInputParams = Parameters<typeof z.date>[0];

/** Mantine DateInput → validated Date (required unless wrapped with `.optional()`). */
export function zDateInput(params?: ZDateInputParams) {
  return z.preprocess(coerceDateInput, z.date(params));
}

/** Mantine DateInput → Date | undefined. */
export function zDateInputOptional() {
  return z.preprocess(coerceDateInput, z.date()).optional();
}

/** Mantine DateInput → Date | null | undefined. */
export function zDateInputNullish() {
  return z.preprocess(coerceDateInput, z.date().nullish());
}

/** Mantine DateInput → Date | null | undefined (explicit null in union). */
export function zDateInputOptionalNullable() {
  return z.preprocess(coerceDateInput, z.union([z.date(), z.null()])).optional();
}

/** Mantine DateInput → YYYY-MM-DD string | undefined (for FHIR valueDate serialization). */
export function zDateInputToISODateOptional() {
  return z.preprocess(
    coerceDateInput,
    z
      .union([z.date(), z.null(), z.undefined()])
      .transform((value) => (value == null ? undefined : zTransformISODate(value))),
  );
}

/** Mantine DateInput → ISO datetime string with local offset | null | undefined. */
export function zDateInputToISODateTimeNullish() {
  return z.preprocess(coerceDateInput, z.date().transform(zTransformISODateTime).nullish());
}

export function zTransformISODate(date: Date | null | undefined) {
  if (!date) return "";
  return formatISO(date, { representation: "date" });
}

/**
 * Transforms a Date to a full ISO 8601 datetime string with local timezone offset
 * (e.g. "2024-03-15T00:00:00-03:00"). Use this instead of zTransformISODate when
 * the date must survive timezone conversion on the receiving end — plain YYYY-MM-DD
 * strings are parsed as UTC midnight by JS engines, which shifts the displayed date
 * by one day for users west of UTC.
 */
export function zTransformISODateTime(date: Date | null | undefined) {
  if (!date) return "";
  return formatISO(date);
}

export function zResource<T extends Resource>(
  resourceType: string,
  message: string = "This field is required",
) {
  return z.custom<T>((data) => {
    return (
      typeof data === "object" && data !== null && (data as Resource).resourceType === resourceType
    );
  }, message);
}
